import { memo, useMemo } from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import { useLanguage } from '../context/LanguageContext'
import { useTranslation } from '../utils/translations'

// Définir les images une seule fois (en dehors du composant)
const serviceImages = [
  'images/services/Gemini_Generated_Image_5lvegq5lvegq5lve.webp',
  'images/services/acompeniement.webp',
  'images/services/Gemini_Generated_Image_8plicw8plicw8pli.webp',
  'images/services/Page_Partenariats-et-reseaux-1-2400x1600.webp',
  'images/services/formation.webp',
  'images/services/recrutement.webp',
]

const fadeIn = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0 },
}

const ServiceCard = ({ icon: Icon, title, description, link, index }) => {
  const { language } = useLanguage()
  const t = useTranslation(language)

  // Optimisation: calcul d'image mémoïsé
  const imageSrc = useMemo(() => {
    return serviceImages[index % serviceImages.length]
  }, [index])

  return (
    <motion.div
      variants={fadeIn}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.1 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      whileHover={{ y: -5, scale: 1.02 }}
      className="group bg-white rounded-3xl p-6 shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 overflow-hidden"
    >
      {/* Image avec effet moderne */}
      <div className="relative h-48 rounded-2xl overflow-hidden mb-6">
        <img
          src={imageSrc}
          alt={title}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
          loading="lazy"
        />
        
        {/* Overlay gradient moderne */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-60 group-hover:opacity-30 transition-opacity duration-500" />
        
        {/* Icon badge moderne */}
        <div className="absolute top-4 right-4 w-12 h-12 bg-white/95 backdrop-blur-sm rounded-xl flex items-center justify-center shadow-lg group-hover:bg-white group-hover:scale-110 transition-all duration-300">
          <Icon size={20} className="text-blue-600" />
        </div>
      </div>

      {/* Content */}
      <div className="space-y-4">
        <h3 className="text-xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors duration-300">
          {title}
        </h3>
        
        <p className="text-gray-600 leading-relaxed text-sm line-clamp-3">
          {description}
        </p>

        {/* Lien moderne */}
        <Link
          to={link}
          className="inline-flex items-center text-blue-600 font-semibold text-sm group/link hover:text-blue-700 transition-all duration-300"
        >
          <span className="relative">
            {t('services.learnMore')}
            <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-blue-600 group-hover/link:w-full transition-all duration-300" />
          </span>
          <ArrowRight
            size={16}
            className="ml-2 transition-transform duration-300 group-hover/link:translate-x-1"
          />
        </Link>
      </div>

      {/* Effet de bordure au hover */}
      <div className="absolute inset-0 rounded-3xl border-2 border-transparent group-hover:border-blue-200 transition-all duration-500 pointer-events-none" />
    </motion.div>
  )
}

export default memo(ServiceCard)